import React from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, FileText, Sparkles, FileCheck } from 'lucide-react';
import Logo from './Logo';

const LandingPage = ({ onGetStarted }) => {
  const { t } = useTranslation('landing');

  const features = [
    {
      icon: Mic,
      titleKey: 'features.voice.title',
      descKey: 'features.voice.description',
    },
    {
      icon: FileText,
      titleKey: 'features.transcription.title',
      descKey: 'features.transcription.description',
    },
    {
      icon: Sparkles,
      titleKey: 'features.enhancement.title',
      descKey: 'features.enhancement.description',
    },
    {
      icon: FileCheck,
      titleKey: 'features.reports.title',
      descKey: 'features.reports.description',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="min-h-screen flex flex-col items-center justify-center px-6 py-16 bg-gradient-to-br from-primary to-[#7a0410] text-white">
        <Logo size={80} className="text-white mb-10" animated />
        <div className="flex items-center gap-4 mb-10">
          <img src="/gtue_white.svg" alt="GTÜ" style={{ height: 25 }} />
          <img src="/slalom_white.svg" alt="Slalom" style={{ height: 14 }} />
        </div>

        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-6 max-w-4xl">
          {t('hero.title')}
        </h1>

        <p className="text-lg md:text-xl text-white/90 text-center mb-10 max-w-2xl">
          {t('hero.subtitle')}
        </p>

        <button
          onClick={onGetStarted}
          className="px-8 py-4 bg-white text-primary-dark font-semibold rounded-lg text-lg
                     hover:bg-gray-100 hover:scale-105 active:scale-95
                     transition-all duration-200 shadow-lg"
        >
          {t('hero.cta')}
        </button>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 animate-bounce">
          <svg
            className="w-6 h-6 text-white/60"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-background">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-text mb-16">
            {t('features.title')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-lg border border-border
                             hover:shadow-md hover:-translate-y-1
                             transition-all duration-200"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-text mb-2">
                    {t(feature.titleKey)}
                  </h3>
                  <p className="text-text-muted text-sm">
                    {t(feature.descKey)}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Flow line connector (visible on lg screens) */}
          <div className="hidden lg:flex justify-center mt-8">
            <div className="flex items-center gap-2">
              {[0, 1, 2].map((i) => (
                <React.Fragment key={i}>
                  <div className="w-16 h-0.5 bg-gradient-to-r from-primary/40 to-primary/20" />
                  <div className="w-2 h-2 rounded-full bg-primary/40" />
                </React.Fragment>
              ))}
              <div className="w-16 h-0.5 bg-gradient-to-r from-primary/40 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#7a0410]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-white mb-8">
            {t('cta.title')}
          </h2>

          <button
            onClick={onGetStarted}
            className="px-8 py-4 bg-primary text-white font-semibold rounded-lg text-lg
                       hover:bg-primary-dark hover:scale-105 active:scale-95
                       transition-all duration-200 shadow-lg"
          >
            {t('hero.cta')}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-[#4a0209]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/gtue_white.svg" alt="GTÜ" style={{ height: 20 }} />
            <img src="/slalom_white.svg" alt="Slalom" style={{ height: 12 }} />
            <span className="text-white/30">|</span>
            <Logo size={24} className="text-white" />
            <span className="text-white font-medium">Holocron</span>
          </div>
          <p className="text-white/60 text-sm">
            &copy; {t('footer.copyright')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
